import { createFileRoute } from '@tanstack/react-router';
import { Fragment } from 'react';
import z from 'zod';

import AngleUpIcon from '@/assets/icons/button-decorators/angle-up.svg?react';
import { DetailMapContentError } from '@/components/errors/DetailMapContentError';
import { DetailMapContentProviderWithPopup } from '@/components/map/DetailMapContentProviderWithPopup';
import { RouteSignDetailMarker } from '@/components/map/RouteSignDetailMarker';
import { RouteSignQrModal } from '@/components/modals/RouteSignQrModal';
import { ReportsList } from '@/components/report/ReportsList';
import { RouteSignFeaturePopup } from '@/components/report/map/RouteSignFeaturePopup';
import { RouteSignFeatureHeader } from '@/components/route-sign/RouteSignFeatureHeader';
import { ButtonLink } from '@/components/router/ButtonLink';
import { useScreenSize } from '@/hooks/useScreenSize';
import { makeMetaTitle } from '@/modules/META';
import { useDuplicateReportsQuery } from '@/queries/useDuplicateReportsQuery';
import { routeSignQueryOptions, useRouteSignQuery } from '@/queries/useRouteSignQuery';

const zValidateSearch = z.object({
  qr: z.boolean().optional().catch(undefined),
  details: z.boolean().optional().catch(undefined),
});

export const Route = createFileRoute('/_public/beschilderung/$routeSignLocationId')({
  validateSearch: zValidateSearch,

  loader: ({ params: { routeSignLocationId }, context: { queryClient } }) => {
    queryClient.ensureQueryData(routeSignQueryOptions({ routeSignLocationId }));
  },

  head: async ({
    params: { routeSignLocationId },
    match: {
      context: { queryClient },
    },
  }) => {
    const routeSign = await queryClient.ensureQueryData(routeSignQueryOptions({ routeSignLocationId }));

    return {
      meta: [makeMetaTitle([`${routeSign.properties.locationId} (${routeSign.properties.municipality})`])],
    };
  },

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
});

function RouteComponent() {
  const params = Route.useParams();
  const search = Route.useSearch();
  const showQrModal = search.qr;
  const showDetails = search.details;
  const { isDesktop } = useScreenSize();

  const { data: routeSignFeature, error } = useRouteSignQuery();

  const queryResult = useDuplicateReportsQuery({ routeSignLocationId: params.routeSignLocationId });

  const buttons = [
    <ButtonLink
      key="create-report"
      startDecorator={<AngleUpIcon />}
      to="/"
      search={{
        standortnummer: params.routeSignLocationId,
      }}
    >
      Meldung erstellen
    </ButtonLink>,
  ];

  return (
    <Fragment>
      <DetailMapContentError error={error} />

      <RouteSignDetailMarker />

      {routeSignFeature ? (
        <DetailMapContentProviderWithPopup
          buttons={buttons}
          popup={<RouteSignFeaturePopup feature={routeSignFeature} />}
        >
          <RouteSignFeatureHeader feature={routeSignFeature} p={2} />

          {(showDetails || isDesktop) && params.routeSignLocationId ? (
            <ReportsList
              noResultsText="Für diese Beschilderung gibt es bisher keine Meldungen."
              queryResult={queryResult}
            />
          ) : null}
        </DetailMapContentProviderWithPopup>
      ) : null}

      {showQrModal ? <RouteSignQrModal /> : null}
    </Fragment>
  );
}
